/**
 * Form state + validação + build dos requests de pessoa relacionada.
 *
 * Add: personId + relationshipType (≥1, enum) + startedAt? + source? — espelha
 * AddRelatedPersonDto. Update: relationshipType? — espelha UpdateRelatedPersonDto.
 * Terminate: endedAt? + reason? — espelha TerminateRelatedPersonDto (ambos
 * OPCIONAIS, sem mínimo de caracteres — difere do revoke do B1).
 *
 * Datas do <input type=date> (YYYY-MM-DD) convertem para ISO (mesmo padrão B1).
 * personId é coletado como texto (honest-defer de person-picker, igual ao B1) —
 * a pessoa relacionada é um TERCEIRO factual, não o titular da tela.
 */

import type {
  AddRelatedPersonRequest,
  UpdateRelatedPersonRequest,
  TerminateRelatedPersonRequest,
} from '../../../lib/api-client';

export interface RelatedPersonFormState {
  personId: string;
  relationshipType: string;
  startedAt: string;
  source: string;
}

export const DEFAULT_SOURCE = 'declared-by-operator';

export function initialAddForm(): RelatedPersonFormState {
  return { personId: '', relationshipType: '', startedAt: '', source: DEFAULT_SOURCE };
}

/** Form de edição pré-preenchido a partir da pessoa existente. startedAt ISO → YYYY-MM-DD. */
export function editFormFrom(rp: {
  personId: string;
  relationshipType: string;
  startedAt: string | null;
  source: string | null;
}): RelatedPersonFormState {
  return {
    personId: rp.personId,
    relationshipType: rp.relationshipType,
    startedAt: rp.startedAt !== null ? isoToDateInput(rp.startedAt) : '',
    source: rp.source ?? DEFAULT_SOURCE,
  };
}

function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM-DD (UTC midnight) → ISO; vazio/inválido → undefined. */
function toISODate(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Validação do form de adição (personId + relationshipType obrigatórios). */
export function validateAddForm(form: RelatedPersonFormState): string | null {
  if (form.personId.trim() === '') return 'Informe o ID da pessoa relacionada.';
  if (form.relationshipType.trim() === '') return 'Selecione o tipo de relacionamento.';
  return null;
}

/** Validação do form de edição (relationshipType obrigatório; personId é imutável aqui). */
export function validateEditForm(form: RelatedPersonFormState): string | null {
  if (form.relationshipType.trim() === '') return 'Selecione o tipo de relacionamento.';
  return null;
}

export function buildAddRequest(form: RelatedPersonFormState): AddRelatedPersonRequest {
  const req: AddRelatedPersonRequest = {
    personId: form.personId.trim(),
    relationshipType: form.relationshipType,
  };
  const startedAt = toISODate(form.startedAt);
  if (startedAt !== undefined) req.startedAt = startedAt;
  if (form.source.trim() !== '') req.source = form.source;
  return req;
}

export function buildUpdateRequest(form: RelatedPersonFormState): UpdateRelatedPersonRequest {
  return { relationshipType: form.relationshipType };
}

export interface TerminateFormState {
  endedAt: string;
  reason: string;
}

export function initialTerminateForm(): TerminateFormState {
  return { endedAt: '', reason: '' };
}

/** Terminate: motivo E data são opcionais (TerminateRelatedPersonDto = @IsOptional). */
export function buildTerminateRequest(form: TerminateFormState): TerminateRelatedPersonRequest {
  const req: TerminateRelatedPersonRequest = {};
  const endedAt = toISODate(form.endedAt);
  if (endedAt !== undefined) req.endedAt = endedAt;
  if (form.reason.trim() !== '') req.reason = form.reason.trim();
  return req;
}
